// Short technique definitions shown inline under each walkthrough step, so a
// reader can see what a technique means without leaving the page. Kept brief on
// purpose — they're the whole definition the walkthrough needs.

export interface TechInfo {
  title: string;
  summary: string;
}

export const TECHNIQUE_INFO: Record<string, TechInfo> = {
  singles: {
    title: "Naked & hidden singles",
    summary:
      "A naked single is a cell with only one letter left; a hidden single is a letter with only one place left in a row, column, or box. Either way the cell is forced.",
  },
  "word-existence": {
    title: "Word existence",
    summary:
      "Each printed word appears exactly once. When only one straight line can still hold a word's letters in order, that placement is forced — drop the whole word in.",
  },
  "word-uniqueness": {
    title: "Word uniqueness",
    summary:
      "Once a word is fully placed it can't appear again, so in any other line that could spell it with one blank left, the completing letter is ruled out.",
  },
  "locked-candidates": {
    title: "Locked candidates",
    summary:
      "If a letter within a 3×3 box is confined to a single row or column, it can be removed from the rest of that line — and vice versa.",
  },
  pairs: {
    title: "Naked & hidden pairs",
    summary:
      "Two cells limited to the same two letters lock those letters out of the rest of the unit; two letters limited to the same two cells clear other candidates from them.",
  },
  triples: {
    title: "Naked & hidden triples",
    summary: "The three-cell version of pairs: three cells (or letters) confined together clear the rest of the unit.",
  },
  quads: {
    title: "Naked & hidden quads",
    summary: "The four-cell version of pairs and triples.",
  },
  "x-wing": {
    title: "X-Wing",
    summary:
      "A letter confined to the same two columns across two rows (or the same two rows across two columns) can be removed from those lines everywhere else.",
  },
  "xy-wing": {
    title: "XY-Wing",
    summary:
      "A pivot cell holding XY with two pincers holding XZ and YZ: whichever letter the pivot takes, one pincer becomes Z, so Z is ruled out of any cell seeing both pincers.",
  },
  swordfish: { title: "Swordfish", summary: "The three-line generalisation of the X-Wing." },
  jellyfish: { title: "Jellyfish", summary: "The four-line generalisation of the X-Wing." },
  "finned-fish": {
    title: "Finned fish",
    summary:
      "An X-Wing, Swordfish, or Jellyfish with one or two extra candidates (the fin); the usual eliminations hold only for cells that also see the fin.",
  },
  "xyz-wing": {
    title: "XYZ-Wing",
    summary: "Like an XY-Wing, but the pivot itself holds all three candidates; the shared letter is ruled out of cells seeing all three.",
  },
  "w-wing": {
    title: "W-Wing",
    summary:
      "Two cells with the same two candidates, joined by a strong link on one of them, force the other candidate out of cells seeing both.",
  },
  "simple-coloring": {
    title: "Simple coloring",
    summary:
      "Follow one letter's conjugate pairs in two alternating colours. A colour that repeats in a unit is impossible, and any cell that sees both colours can't hold the letter.",
  },
  "x-chain": {
    title: "X-Chain",
    summary: "An alternating strong/weak-link chain on one letter; one endpoint must hold it, so it's removed from cells seeing both ends.",
  },
  "xy-chain": {
    title: "XY-Chain",
    summary: "A chain of two-candidate cells; when both ends offer the same letter, it's ruled out of every cell seeing both ends.",
  },
  "3d-medusa": {
    title: "3D Medusa",
    summary: "Two-colour clustering across all candidates and links at once; contradictions, or cells seeing both colours, drive the eliminations.",
  },
  "als-xz": {
    title: "ALS-XZ",
    summary:
      "Two almost-locked sets sharing a restricted common candidate force a second shared candidate to be confined, ruling it out of cells that see all of it.",
  },
};
